import {DriverActivity} from "../../../model/driver-activity";
import {TransportSearchCriteria} from "../../../model/search/tsc";
import {otsBaseApi} from "../../ots-api";
import {OtsDispatch} from "../../ots-store";
import {EndpointDefinitions, RootState} from "@reduxjs/toolkit/query";
import {adjustToTimeZone} from "../../../date/date";
import {ONE_DAY_IN_MILLIS} from "../../../util/constants";
import {Draft} from "@reduxjs/toolkit";
import {Transport} from "../../../model/transport";
import {Message} from "../../../model/message";
import {Shift} from "../../../model/shift";
import {TransportAssoc} from "../../../model/transport-assoc";

const taggedBaseApi = otsBaseApi.enhanceEndpoints({addTagTypes: ["DriverActivity"]});

export const driverActivityApi = taggedBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    searchDriverActivity: builder.query<
      DriverActivity[],
      TransportSearchCriteria
    >({
      query: (criteria) => ({
        url: `/driver-activity/search`,
        method: 'POST',
        body: criteria,
      }),
      providesTags: (result) => {
        return [{type: "DriverActivity", id: "LIST"}];
      },
      keepUnusedDataFor: 120,
    }),
    getDriverActivityByDay: builder.query<
      DriverActivity[],
      {
        day: number,
        regionId: string
        startShiftReport?: boolean
      }
    >({
      query: (criteria) => ({
        url: `/driver-activity/search`,
        method: 'POST',
        body: {
          fromDttm: adjustToTimeZone(criteria.day).getTime(),
          toDttm: adjustToTimeZone(criteria.day).getTime() + ONE_DAY_IN_MILLIS,
          startShiftReport: criteria.startShiftReport,
          regionId: criteria.regionId,
          showHoldings: false,
          showTabletHoldings: false,
        },
      }),
      providesTags: (result, _error, arg) => {
        return [{type: "DriverActivity", id: `LIST_${arg.day}_${arg.regionId}`}];
      },
      keepUnusedDataFor: 120,
    }),
  })
});

const usePrefetch = driverActivityApi.usePrefetch;
export {usePrefetch as useDriverActivityPrefetch};

export const {
  useSearchDriverActivityQuery,
  useGetDriverActivityByDayQuery,
  useLazySearchDriverActivityQuery,
} = driverActivityApi;

/**
 * Merges a saved transport into a day of driver activity, wherever it appears: a ticket's pickup or delivery,
 * or an unmatched pickup or delivery row. Fields the save did not send back (such as gauges and messages)
 * are kept, so the row stays whole without fetching the day again.
 */
export const mergeTransportIntoDriverActivity = (rows: DriverActivity[] | Draft<DriverActivity[]>, transport: Transport) => {
  for (const row of rows) {
    for (const holder of [row, row.transportTransportAssoc]) {
      if (holder?.pickupTransport?.id === transport.id) Object.assign(holder.pickupTransport, transport);
      if (holder?.deliveryTransport?.id === transport.id) Object.assign(holder.deliveryTransport, transport);
    }
  }
};

/** Adds a new comment to the transport it is on, wherever that transport appears in a day of driver activity. */
export const mergeMessageIntoDriverActivity = (rows: DriverActivity[] | Draft<DriverActivity[]>, message: Message) => {
  for (const row of rows) {
    for (const transport of [row.pickupTransport, row.deliveryTransport, row.transportTransportAssoc?.pickupTransport, row.transportTransportAssoc?.deliveryTransport]) {
      if (transport?.id === message.objectId) transport.messages = [...(transport.messages ?? []), message];
    }
  }
};

/**
 * The span and region a cached driver activity query covers, for deciding whether a record saved elsewhere
 * belongs in it. Only the day query is described: a search can filter on anything, so it is refetched instead.
 */
export type DriverActivityWindow = {
  fromDttm: number;
  toDttm: number;
  regionId?: string;
  /** The day lists shifts that started in it, with everything in them, rather than what happened in it. */
  startShiftReport?: boolean;
};

export const driverActivityQueryWindow = (endpointName: string, args: unknown): DriverActivityWindow | undefined => {
  if (endpointName !== "getDriverActivityByDay" || !args) return undefined;
  const {day, regionId, startShiftReport} = args as {day: number, regionId: string, startShiftReport?: boolean};
  const fromDttm = adjustToTimeZone(day).getTime();
  return {fromDttm, toDttm: fromDttm + ONE_DAY_IN_MILLIS, regionId, startShiftReport};
};

const within = (window: DriverActivityWindow, time: number | undefined) =>
  time != null && time >= window.fromDttm && time < window.toDttm;

const inRegion = (window: DriverActivityWindow, regionId: string | undefined) =>
  !window.regionId || !regionId || window.regionId === regionId;

/** When a row happened: what places it among the rest of its driver's day. */
export const driverActivityRowTime = (row: DriverActivity): number | undefined =>
  row.startShift?.startDttm
  ?? row.endShift?.endDttm
  ?? row.transportTransportAssoc?.pickupTransport?.createDttm
  ?? row.transportTransportAssoc?.deliveryTransport?.createDttm
  ?? row.pickupTransport?.createDttm
  ?? row.deliveryTransport?.createDttm
  ?? row.geofenceEvent?.inDttm
  ?? row.serviceWorkStart?.startDttm
  ?? row.serviceWorkEnd?.endDttm
  ?? row.containerVolumeDrop?.startDttm
  ?? row.dispatch?.scheduleDttm;

const driverActivityRowPersonId = (row: DriverActivity): string | undefined =>
  row.startShift?.personId
  ?? row.endShift?.personId
  ?? row.transportTransportAssoc?.pickupTransport?.personId
  ?? row.transportTransportAssoc?.deliveryTransport?.personId
  ?? row.pickupTransport?.personId
  ?? row.deliveryTransport?.personId
  ?? row.geofenceEvent?.personId
  ?? row.serviceWorkStart?.personId
  ?? row.serviceWorkEnd?.personId
  ?? row.dispatch?.personId;

/**
 * Puts a new row where the server would have listed it: after the last of the same driver's rows that is not
 * later than it, or at the end for a driver with nothing else that day. A shift's start goes before activity at
 * the same moment and its end after, so the shift still wraps what it contains.
 */
export const insertDriverActivityRow = (rows: DriverActivity[] | Draft<DriverActivity[]>, row: DriverActivity) => {
  const personId = driverActivityRowPersonId(row);
  const time = driverActivityRowTime(row) ?? Number.MAX_SAFE_INTEGER;
  // The index to insert at: after the driver's last row that comes first, else before their first row.
  let after: number | undefined;
  let before: number | undefined;
  rows.forEach((candidate, candidateIndex) => {
    if (driverActivityRowPersonId(candidate) !== personId) return;
    const candidateTime = driverActivityRowTime(candidate) ?? Number.MAX_SAFE_INTEGER;
    if (candidateTime < time || (candidateTime === time && !row.startShift)) after = candidateIndex + 1;
    else before ??= candidateIndex;
  });
  rows.splice(after ?? before ?? rows.length, 0, row as Draft<DriverActivity>);
};

const removeRows = (rows: DriverActivity[] | Draft<DriverActivity[]>, matches: (row: DriverActivity) => boolean) => {
  let removed: DriverActivity | undefined;
  for (let index = rows.length - 1; index >= 0; index--) {
    if (matches(rows[index] as DriverActivity)) removed = rows.splice(index, 1)[0] as DriverActivity;
  }
  return removed;
};

/**
 * Puts a saved shift into a day: its start and end rows replace any it had, so an edit that moves it moves its
 * rows, and a new shift appears without the day being fetched again. A shift shows in a day when it starts or
 * ends in it, or, for a start-shift report, when it starts in it. With no window (a filtered search) the shift
 * is only updated where it already is.
 */
export const upsertShiftIntoDriverActivity = (rows: DriverActivity[] | Draft<DriverActivity[]>, shift: Shift, window: DriverActivityWindow | undefined) => {
  const previous = rows.find((row) => row.startShift?.id === shift.id || row.endShift?.id === shift.id);
  if (!window) {
    for (const row of rows) {
      if (row.startShift?.id === shift.id) Object.assign(row.startShift, shift);
      if (row.endShift?.id === shift.id) Object.assign(row.endShift, shift);
    }
    return;
  }
  removeRows(rows, (row) => row.startShift?.id === shift.id || row.endShift?.id === shift.id);
  if (!inRegion(window, shift.regionId)) return;
  // Keep the server-built summaries the save does not send back.
  const merged = {...(previous?.startShift ?? previous?.endShift), ...shift} as Shift;
  const showStart = within(window, merged.startDttm);
  const showEnd = merged.endDttm != null && (window.startShiftReport ? showStart : within(window, merged.endDttm));
  if (showStart) insertDriverActivityRow(rows, {startShift: merged});
  if (showEnd) insertDriverActivityRow(rows, {endShift: {...merged}});
};

const ttaTransportIds = (tta: TransportAssoc) =>
  new Set([tta.pickupTransport?.id ?? tta.pickupTransportId, tta.deliveryTransport?.id ?? tta.deliveryTransportId].filter(Boolean));

/**
 * Puts a saved ticket into a day: it replaces its own row and the unmatched pickup or delivery rows it now
 * joins, and is added when it belongs to the day: its pickup (or its delivery, without one) falls in it, or, for
 * a start-shift report, its shift started in it.
 */
export const upsertTtaIntoDriverActivity = (rows: DriverActivity[] | Draft<DriverActivity[]>, tta: TransportAssoc, window: DriverActivityWindow | undefined) => {
  const ids = ttaTransportIds(tta);
  const isOwnRow = (row: DriverActivity) => row.transportTransportAssoc?.id === tta.id
    || (!row.transportTransportAssoc && (ids.has(row.pickupTransport?.id) || ids.has(row.deliveryTransport?.id)));
  if (!window) {
    const index = rows.findIndex((row) => row.transportTransportAssoc?.id === tta.id);
    if (index >= 0) rows[index].transportTransportAssoc = tta as Draft<TransportAssoc>;
    return;
  }
  const previous = removeRows(rows, isOwnRow);
  const time = tta.pickupTransport?.createDttm ?? tta.deliveryTransport?.createDttm;
  const regionId = tta.regionId ?? tta.pickupTransport?.regionId ?? tta.deliveryTransport?.regionId;
  const belongs = inRegion(window, regionId)
    && (window.startShiftReport && tta.startShiftDttm != null ? within(window, tta.startShiftDttm) : within(window, time));
  if (belongs) insertDriverActivityRow(rows, {warnings: previous?.transportTransportAssoc ? previous.warnings : undefined, transportTransportAssoc: tta});
};

/** Splits a ticket back into its pickup and delivery rows, after the association between them is removed. */
export const splitTtaInDriverActivity = (rows: DriverActivity[] | Draft<DriverActivity[]>, ttaId: string) => {
  const index = rows.findIndex((row) => row.transportTransportAssoc?.id === ttaId);
  if (index < 0) return;
  const {pickupTransport, deliveryTransport} = rows[index].transportTransportAssoc ?? {};
  rows.splice(index, 1);
  if (pickupTransport) insertDriverActivityRow(rows, {pickupTransport: pickupTransport as Transport});
  if (deliveryTransport) insertDriverActivityRow(rows, {deliveryTransport: deliveryTransport as Transport});
};

/** Puts a newly created pickup or delivery into a day when it was made for it; an existing one is merged in place. */
export const upsertTransportIntoDriverActivity = (rows: DriverActivity[] | Draft<DriverActivity[]>, transport: Transport, window: DriverActivityWindow | undefined) => {
  const present = rows.some((row) => [row.pickupTransport, row.deliveryTransport, row.transportTransportAssoc?.pickupTransport, row.transportTransportAssoc?.deliveryTransport]
    .some((candidate) => candidate?.id === transport.id));
  if (present) {
    mergeTransportIntoDriverActivity(rows, transport);
    return;
  }
  if (!window || !inRegion(window, transport.regionId) || !within(window, transport.createDttm)) return;
  insertDriverActivityRow(rows, transport.type === "DELIVERY" ? {deliveryTransport: transport} : {pickupTransport: transport});
};

/**
 * Applies a change to every cached driver activity query. The recipe is told the window of a day query, so it
 * can add a record that belongs there; a search has none.
 */
export const driverActivityCacheUpdate = (dispatch: OtsDispatch, state: RootState<EndpointDefinitions, string, string>, updateRecipe: (data: (DriverActivity[] | Draft<DriverActivity[]>), window?: DriverActivityWindow) => void | (DriverActivity[] | Draft<DriverActivity[]>)) => {
  const patchResults = []
  const queries = driverActivityApi.util.selectInvalidatedBy(state, [{type: "DriverActivity"}])
  for (const query of queries) {
    const {endpointName, originalArgs} = query
    if (endpointName === 'searchDriverActivity') {
      patchResults.push(dispatch(
        driverActivityApi.util.updateQueryData('searchDriverActivity', originalArgs, (draft) => updateRecipe(draft))
      ))
    } else if (endpointName === 'getDriverActivityByDay') {
      patchResults.push(dispatch(
        driverActivityApi.util.updateQueryData('getDriverActivityByDay', originalArgs, (draft) => updateRecipe(draft, driverActivityQueryWindow(endpointName, originalArgs)))
      ))
    }
  }
  return patchResults
}
